import clsx from 'clsx'
import { type FC, useState, useRef, useEffect } from 'react'
import { IconPaths, InlineSvg, toAbsoluteUrl } from '_metronic/helpers'
import { useT } from '_metronic/i18n/Metronici18n'
import { useChamadaAtivaOpcional } from '../../chamada-ativa/ChamadaAtivaProvider'
import { ControlesPip } from '../../chamada-ativa/components/ControlesPip'
import { type BackgroundType } from '../types'
import { PATIENT_VOLUME_DEFAULT, PATIENT_VOLUME_MAX } from '../constants'
import styles from './styles/AgoraVideoContainer.module.css'
import { VideoCallIcon } from './videoCallIcons'

const clampPatientVolume = (value: number): number =>
  Math.min(PATIENT_VOLUME_MAX, Math.max(0, Number.isNaN(value) ? 0 : value))

const rotuloGravacao = (isGravacaoAtiva?: boolean, gravacaoPendente?: boolean): string => {
  if (gravacaoPendente) {
    return isGravacaoAtiva ? 'Encerrando gravação da consulta' : 'Iniciando gravação da consulta'
  }
  return isGravacaoAtiva ? 'Parar gravação da consulta' : 'Iniciar gravação da consulta'
}

const rotuloStt = (isSttEnabled?: boolean, sttPendente?: boolean): string => {
  if (sttPendente) {
    return isSttEnabled ? 'Desativando transcrição e legendas' : 'Ativando transcrição e legendas'
  }
  return isSttEnabled ? 'Desativar transcrição e legendas' : 'Ativar transcrição e legendas'
}

type VideoControlsProps = {
  isMicEnabled: boolean
  isVideoEnabled: boolean
  isPaciente: boolean
  hasProcessor: boolean
  backgroundType: BackgroundType
  showBackgroundMenu: boolean
  chatOpen: boolean
  unreadCount: number
  isSttEnabled?: boolean
  sttPendente?: boolean
  onToggleStt?: () => void
  onToggleMic: () => void
  onToggleVideo: () => void
  onToggleBackgroundMenu: () => void
  onToggleChat: () => void
  isScreenSharing: boolean
  onToggleScreenShare: () => void
  isMaximized?: boolean
  onToggleMaximize?: () => void
  patientVolume?: number
  onChangeVolume?: (volume: number) => void
  isGravacaoAtiva?: boolean
  gravacaoPendente?: boolean
  onToggleGravacao?: () => void
}

const BarraDeControles: FC<VideoControlsProps> = ({
  isMicEnabled,
  isVideoEnabled,
  isPaciente,
  hasProcessor,
  backgroundType,
  showBackgroundMenu,
  chatOpen,
  unreadCount,
  isSttEnabled,
  sttPendente,
  onToggleStt,
  onToggleMic,
  onToggleVideo,
  onToggleBackgroundMenu,
  onToggleChat,
  isScreenSharing,
  onToggleScreenShare,
  isMaximized,
  onToggleMaximize,
  patientVolume,
  onChangeVolume,
  isGravacaoAtiva,
  gravacaoPendente,
  onToggleGravacao,
}) => {
  const [showVolumeSlider, setShowVolumeSlider] = useState(false)
  const volumeControlRef = useRef<HTMLDivElement>(null)
  // PiP da chamada persistente (LO-829): lido do contexto, e não por props, para não exigir repasse em cada
  // wrapper do player (main: video-atendimento-agora; develop: AgoraVideoContainer). Fora da área logada o
  // contexto não existe e o botão não aparece.
  const chamada = useChamadaAtivaOpcional()
  const { t } = useT()
  const podeMinimizar =
    !isPaciente && chamada !== undefined && chamada.atendimentoId !== null && chamada.pipSuportado
  const rotuloMinimizar = t('atendimento.chamada.minimizar')

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (volumeControlRef.current && !volumeControlRef.current.contains(event.target as Node)) {
        setShowVolumeSlider(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  return (
    <div className={`position-absolute d-flex align-items-center gap-2 ${styles.controls}`}>
      <button
        onClick={onToggleMic}
        title='Microfone'
        className={`position-relative border-0 bg-transparent ${styles.controlButton}`}
        type='button'
      >
        <i className={isMicEnabled ? VideoCallIcon.Mic : VideoCallIcon.MicOff} />
      </button>

      <button
        onClick={onToggleVideo}
        title='Vídeo'
        className={`position-relative border-0 bg-transparent ${styles.controlButton}`}
        type='button'
      >
        <i className={isVideoEnabled ? VideoCallIcon.Video : VideoCallIcon.VideoOff} />
      </button>

      {!isPaciente && onChangeVolume && patientVolume !== undefined && (
        <div ref={volumeControlRef} className='position-relative d-flex align-items-center'>
          <button
            onClick={() => setShowVolumeSlider(!showVolumeSlider)}
            title='Volume do Paciente'
            className={clsx(
              'position-relative border-0 bg-transparent',
              styles.controlButton,
              (patientVolume === 0 || showVolumeSlider) && styles.active
            )}
            type='button'
          >
            <i
              className={clsx(
                patientVolume === 0 && [VideoCallIcon.VolumeMute, 'text-danger'],
                patientVolume > 0 &&
                  patientVolume <= PATIENT_VOLUME_DEFAULT &&
                  VideoCallIcon.VolumeLow,
                patientVolume > PATIENT_VOLUME_DEFAULT && [VideoCallIcon.VolumeHigh, 'text-warning']
              )}
            />
          </button>

          {showVolumeSlider && (
            <div className='position-absolute top-100 mt-2 d-flex flex-column align-items-center rounded shadow-sm bg-body border start-50 translate-middle-x z-3 py-3 px-2 min-w-40px'>
              <input
                type='number'
                min='0'
                max={PATIENT_VOLUME_MAX}
                value={patientVolume}
                onChange={(e) => onChangeVolume(clampPatientVolume(parseInt(e.target.value, 10)))}
                className={clsx(
                  'w-100 bg-transparent border-0 text-center mb-3 fw-bold fs-8 outline-none',
                  patientVolume > PATIENT_VOLUME_DEFAULT ? 'text-warning' : 'text-gray-800'
                )}
              />
              <div className='position-relative d-flex justify-content-center align-items-center mx-auto h-100px w-25px'>
                <input
                  type='range'
                  min='0'
                  max={PATIENT_VOLUME_MAX}
                  step='1'
                  value={patientVolume}
                  onChange={(e) => onChangeVolume(Number(e.target.value))}
                  className={clsx(
                    'form-range position-absolute m-0 cursor-pointer',
                    styles.volumeSlider
                  )}
                  style={{
                    background: `linear-gradient(to right, var(--bs-primary) ${
                      (patientVolume / PATIENT_VOLUME_MAX) * 100
                    }%, rgba(var(--bs-gray-500-rgb), 0.3) ${
                      (patientVolume / PATIENT_VOLUME_MAX) * 100
                    }%)`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {!isPaciente && hasProcessor && (
        <button
          onClick={onToggleBackgroundMenu}
          title='Fundo Virtual'
          className={clsx(
            'position-relative border-0 bg-transparent',
            styles.controlButton,
            (showBackgroundMenu || backgroundType !== 'none') && styles.active
          )}
          type='button'
        >
          <i className={VideoCallIcon.VirtualBackground} />
        </button>
      )}

      {!isPaciente && (
        <button
          onClick={onToggleScreenShare}
          title={isScreenSharing ? 'Parar Compartilhamento' : 'Compartilhar Tela'}
          className={`position-relative border-0 bg-transparent ${styles.controlButton}`}
          type='button'
        >
          <i className={isScreenSharing ? VideoCallIcon.Stop : VideoCallIcon.ScreenShare} />
        </button>
      )}

      {podeMinimizar && (
        <button
          onClick={chamada.minimizar}
          aria-label={rotuloMinimizar}
          title={rotuloMinimizar}
          className={`position-relative border-0 bg-transparent ${styles.controlButton}`}
          type='button'
        >
          <i className={VideoCallIcon.PictureInPicture} aria-hidden='true' />
        </button>
      )}

      {!isPaciente && onToggleMaximize && (
        <button
          onClick={onToggleMaximize}
          title={
            isMaximized ? 'Restaurar Tamanho da Câmera do Paciente' : 'Maximizar Câmera do Paciente'
          }
          className={`position-relative border-0 bg-transparent ${styles.controlButton}`}
          type='button'
        >
          <i className={isMaximized ? VideoCallIcon.Minimize : VideoCallIcon.Maximize} />
        </button>
      )}

      {!isPaciente && onToggleGravacao && (
        <button
          onClick={onToggleGravacao}
          disabled={gravacaoPendente}
          aria-pressed={isGravacaoAtiva}
          aria-busy={gravacaoPendente}
          aria-label={rotuloGravacao(isGravacaoAtiva, gravacaoPendente)}
          title={rotuloGravacao(isGravacaoAtiva, gravacaoPendente)}
          className={clsx(
            'position-relative border-0 bg-transparent',
            styles.controlButton,
            isGravacaoAtiva && styles.active,
            gravacaoPendente && `text-white ${styles.controlButtonPendente}`
          )}
          type='button'
        >
          {gravacaoPendente ? (
            <span className='spinner-border spinner-border-sm align-middle' aria-hidden='true' />
          ) : (
            <i
              className={
                isGravacaoAtiva ? clsx(VideoCallIcon.Stop, 'text-danger') : VideoCallIcon.Record
              }
            />
          )}
        </button>
      )}

      {!isPaciente && <span className='vr opacity-25 mx-1' />}

      {!isPaciente && (
        <button
          onClick={onToggleStt}
          disabled={sttPendente}
          aria-pressed={isSttEnabled}
          aria-busy={sttPendente}
          aria-label={rotuloStt(isSttEnabled, sttPendente)}
          className={clsx(
            'position-relative border-0 bg-transparent',
            styles.controlButton,
            isSttEnabled && styles.active
          )}
          title={rotuloStt(isSttEnabled, sttPendente)}
          type='button'
        >
          {sttPendente ? (
            <span className='spinner-border spinner-border-sm align-middle' aria-hidden='true' />
          ) : isSttEnabled ? (
            <span className={clsx('fa-stack', styles.iconeCortado)} aria-hidden='true'>
              <i className={clsx(VideoCallIcon.Captions, 'fa-stack-1x')} />
              <InlineSvg
                src={toAbsoluteUrl(IconPaths.CorteDiagonal)}
                className={styles.corteDiagonal}
              />
            </span>
          ) : (
            <i className={VideoCallIcon.Captions} aria-hidden='true' />
          )}
        </button>
      )}

      <button
        onClick={onToggleChat}
        title='Chat'
        className={`position-relative border-0 bg-transparent ${styles.controlButton}`}
        type='button'
      >
        <i className={chatOpen ? VideoCallIcon.ChatOff : VideoCallIcon.Chat} />
        {unreadCount > 0 && !chatOpen && (
          <span className='badge badge-circle badge-danger position-absolute top-0 start-100 translate-middle fs-9'>
            {unreadCount}
          </span>
        )}
      </button>
    </div>
  )
}

/**
 * Barra do player + microfone e câmera próprios da janela PiP (LO-829). Os do PiP ficam fora da barra: fora
 * da tela do atendimento, as regras "só vídeo" escondem a barra inteira.
 */
export const VideoControls: FC<VideoControlsProps> = (props) => {
  const chamada = useChamadaAtivaOpcional()
  const emChamadaPersistente =
    !props.isPaciente && chamada !== undefined && chamada.atendimentoId !== null

  return (
    <>
      {emChamadaPersistente && (
        <ControlesPip
          isMicEnabled={props.isMicEnabled}
          isVideoEnabled={props.isVideoEnabled}
          onToggleMic={props.onToggleMic}
          onToggleVideo={props.onToggleVideo}
        />
      )}
      <BarraDeControles {...props} />
    </>
  )
}
